'use client'

import * as React from 'react'
import Link from 'next/link'
import { Crown, LogOut, Smartphone, Sparkles } from 'lucide-react'
import { usePathname, useRouter } from 'next/navigation'
import { toast } from 'sonner'
import { NotificationCenter } from '@/components/feedback/notification-center'
import { usePwaInstall } from '@/hooks/use-pwa-install'
// HAB-54: PwaInstallModal lazy-loaded — only opens when user clicks "Add app to phone".
// Removes ~9kB of FormModal/Button chain + 6 lucide icons from the eager header chunk.
const PwaInstallModal = React.lazy(() =>
  import('@/components/layout/pwa-install-modal').then(m => ({ default: m.PwaInstallModal })),
)
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Drawer,
  DrawerContent,
  DrawerClose,
  DrawerDescription,
  DrawerTitle,
  DrawerTrigger,
} from '@/components/ui/drawer'

import { useAuth } from '@/features/auth/hooks/use-auth'
import { useUserProfile, updateAppSessionState } from '@/state/session/context'
import { getCurrentNavigationItem, getProfileMenuItems } from '@/lib/config/navigation'
import { cn } from '@/lib/utils'
import { getXpProgress, XP_PER_LEVEL } from '@/lib/core-loop/xp-progress'
import { AnimatedProgressBar } from '@/components/ui/animated-progress-bar'

function HabitpackLogoIcon({ className }: { className?: string }): React.JSX.Element {
  return (
    <span
      aria-hidden
      className={cn('inline-block bg-current', className)}
      style={{
        maskImage: "url('/logo.svg')",
        WebkitMaskImage: "url('/logo.svg')",
        maskRepeat: 'no-repeat',
        WebkitMaskRepeat: 'no-repeat',
        maskPosition: 'center',
        WebkitMaskPosition: 'center',
        maskSize: 'contain',
        WebkitMaskSize: 'contain',
      }}
    />
  )
}

export function AppHeader(): React.JSX.Element {
  const router = useRouter()
  const pathname = usePathname()
  const userProfile = useUserProfile()
  const { signOut } = useAuth()
  const [profileOpen, setProfileOpen] = React.useState(false)
  const [isSigningOut, setIsSigningOut] = React.useState(false)
  const [animatingXp, setAnimatingXp] = React.useState(true)
  const [pwaModalOpen, setPwaModalOpen] = React.useState(false)
  const pwa = usePwaInstall()

  // Check if PWA reward has been claimed — read from session profile.
  // `isPwaActivated` is an optional flag that lives on the session profile but
  // isn't part of the canonical UserProfile shape, so type it locally.
  const isPwaActivated = Boolean(
    (userProfile as (typeof userProfile & { isPwaActivated?: boolean }) | null)?.isPwaActivated,
  )

  React.useEffect(() => {
    const timer = setTimeout(() => setAnimatingXp(false), 500)
    return () => clearTimeout(timer)
  }, [])

  const navItem = getCurrentNavigationItem(pathname)

  const user =
    userProfile ?? {
      displayName: 'Aventureiro',
      username: 'habitpack',
      avatarUrl: '/placeholder-user.jpg',
      annualLevel: 1,
      annualXp: 0,
      annualXpToNext: 500,
      lifetimeLevel: 1,
      habitCoins: 0,
      hasPremium: false,
      isLifetimePass: false,
      passDaysRemaining: 0,
    }

  const avatarSrc = user.avatarUrl || '/placeholder-user.jpg'
  const profileItems = getProfileMenuItems(user.username)
  const annualXp = Math.max(0, Number(user.annualXp ?? 0))
  const xpProgress = getXpProgress(annualXp)
  const xpPercent = xpProgress.percent
  const currentLevel = xpProgress.computedLevel
  const premiumBadgeLabel = user.isLifetimePass
    ? 'Pass • Eterno'
    : `Pass • ${Math.max(0, Number(user.passDaysRemaining ?? 0))}d`
  const coinsLabel = user.habitCoins.toLocaleString('pt-BR')

  const handleSignOut = () => {
    if (isSigningOut) return
    setIsSigningOut(true)
    setProfileOpen(false)
    updateAppSessionState((prev) => ({
      ...prev,
      hydrated: true,
      isLoading: true,
      postOnboardingRedirect: null,
    }))

    signOut()
      .then(() => {
        toast.success('Sessão encerrada com sucesso.')
        router.replace('/login')
      })
      .catch(() => {
        updateAppSessionState((prev) => ({
          ...prev,
          hydrated: true,
          isLoading: false,
        }))
        setIsSigningOut(false)
        toast.error('Não foi possível encerrar a sessão agora.')
      })
  }

  return (
    <header className="sticky top-0 z-40 border-b border-border bg-background">

      {/* Desktop */}
      <div className="hidden min-h-[60px] items-center gap-4 px-6 lg:flex">

        {/* Identity */}
        <div className="flex shrink-0 items-center gap-3">
          <Link
            href="/dashboard"
            className="flex size-9 shrink-0 items-center justify-center rounded-xl border border-primary/35 bg-tint-primary-subtle shadow-sm transition-colors hover:bg-primary/18"
            aria-label="Início"
          >
            <HabitpackLogoIcon className="size-6 text-primary" />
          </Link>
          <div className="leading-none">
            <p className="text-xs font-black tracking-[0.2em] text-primary uppercase">HabitPack</p>
            <p className="mt-0.5 text-sm font-bold text-foreground">{navItem.title}</p>
          </div>
        </div>

        {/* XP bar — compact single row */}
        <div className="flex min-w-[160px] max-w-[220px] shrink-0 items-center gap-2" data-testid="user-xp">
        <Link
          href="/emporium/profile"
          className="shrink-0 text-xs font-bold text-muted-foreground hover:text-primary transition-colors"
          title={`${xpProgress.xpInCurrentLevel.toLocaleString('pt-BR')} / ${XP_PER_LEVEL.toLocaleString('pt-BR')} XP (${xpPercent}%)`}
          data-testid="user-xp-level"
        >
          Nv.{currentLevel}
        </Link>
          <div className="min-w-[60px] flex-1">
            <AnimatedProgressBar
              value={xpPercent}
              className="h-[5px]"
              indicatorClassName="bg-primary"
              animateOnMount={animatingXp}
              label="Progresso de XP"
              valueText={`${xpProgress.xpInCurrentLevel.toLocaleString('pt-BR')} / ${XP_PER_LEVEL.toLocaleString('pt-BR')} XP`}
            />
          </div>
          <span className="shrink-0 text-xs font-bold text-muted-foreground">{xpPercent}%</span>
        </div>

        <div className="flex-1" />

        {/* Actions */}
        <div className="flex items-center gap-2">

          {/* Coins */}
          <button
            type="button"
            onClick={() => router.push('/emporium/shop')}
            className="flex h-8 items-center gap-1.5 rounded-md border border-coin/35 bg-card px-2.5 text-xs font-bold text-coin transition-colors transition-transform hover:-translate-y-0.5 hover:border-coin/60 hover:bg-coin/10"
            aria-label="Abrir loja"
          >
            <Sparkles className="size-3 shrink-0" aria-hidden="true" />
            {coinsLabel} HC
          </button>

          {/* Pass */}
          {user.hasPremium ? (
            <div className="flex h-8 items-center gap-1.5 rounded-md border border-primary/45 bg-tint-primary-subtle px-2.5 text-xs font-bold text-primary">
              <Crown className="size-3 shrink-0" aria-hidden="true" />
              {premiumBadgeLabel}
            </div>
          ) : (
            <button
              type="button"
              onClick={() => router.push('/emporium/pass')}
              className="flex h-8 items-center gap-1.5 rounded-md border border-premium bg-premium/10 px-2.5 text-xs font-bold text-premium transition-colors transition-transform hover:-translate-y-0.5 hover:bg-tint-primary-subtle"
            >
              <Crown className="size-3 shrink-0" aria-hidden="true" />
              Assinar Pass
            </button>
          )}

          {/* Notifications */}
          <NotificationCenter
            className="relative flex size-8 items-center justify-center rounded-md border border-border bg-card text-muted-foreground transition-colors transition-transform hover:-translate-y-0.5 hover:border-primary/50 hover:text-primary"
          />

          <div className="h-6 w-px bg-border" />

          {/* Avatar + dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="flex items-center gap-2 rounded-md px-1.5 py-1 transition-colors hover:bg-muted/50"
                aria-label="Abrir menu do perfil"
              >
                <div className="max-w-[140px] text-left leading-none">
                  <p className="truncate text-xs font-bold text-foreground">{user.displayName}</p>
                  <p className="text-xs font-black text-primary uppercase">@{user.username}</p>
                </div>
                <img
                  src={avatarSrc}
                  alt={
                    user.displayName
                      ? `Avatar de ${user.displayName}`
                      : `Avatar do usuário @${user.username}`
                  }
                  loading="lazy"
                  decoding="async"
                  className="size-8 rounded-full border border-primary/40 object-cover"
                />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-60">
              <DropdownMenuLabel>
                <div className="flex flex-col gap-0.5">
                  <p className="text-sm font-bold text-foreground">{user.displayName}</p>
                  <p className="text-xs text-muted-foreground">
                    @{user.username} · Nv.{currentLevel} · Eterno {user.lifetimeLevel}
                  </p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuGroup>
              {profileItems.map((item) => (
                <DropdownMenuItem key={item.href} asChild>
                  <Link href={item.href}>
                    <item.icon className="size-4" aria-hidden="true" />
                    <span>{item.label}</span>
                  </Link>
                </DropdownMenuItem>
              ))}
              </DropdownMenuGroup>
              <DropdownMenuSeparator />
              <DropdownMenuItem variant="destructive" onSelect={handleSignOut}>
                <LogOut className="size-4" aria-hidden="true" />
                <span>Sair da conta</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Mobile */}
      <div className="flex min-h-[60px] items-center gap-2 px-4 lg:hidden">

        {/* Identity + XP */}
        <div className="flex min-w-0 flex-1 items-center gap-2.5">
          <Link
            href="/dashboard"
            className="flex size-9 shrink-0 items-center justify-center rounded-xl border border-primary/35 bg-tint-primary-subtle"
            aria-label="Início"
          >
            <HabitpackLogoIcon className="size-6 text-primary" />
          </Link>
          <div className="min-w-0 flex-1">
           <p className="text-xs font-black leading-none tracking-[0.14em] text-primary uppercase">
             HabitPack
           </p>
            <p className="mt-0.5 truncate text-xs font-bold leading-tight text-foreground">
              {navItem.title}
            </p>
            <div className="mt-1 flex items-center gap-1.5" data-testid="user-xp">
              <Link
                href="/emporium/profile"
                className="shrink-0 text-xs font-bold text-muted-foreground hover:text-primary transition-colors"
                title={`${xpProgress.xpInCurrentLevel.toLocaleString('pt-BR')} / ${XP_PER_LEVEL.toLocaleString('pt-BR')} XP (${xpPercent}%)`}
                data-testid="user-xp-level"
              >
                Nv.{currentLevel}
              </Link>
              <div className="min-w-[40px] max-w-[80px] sm:max-w-[120px] flex-1">
                <AnimatedProgressBar
                  value={xpPercent}
                  className="h-[4px]"
                  indicatorClassName="bg-primary"
                  animateOnMount={animatingXp}
                  label="Progresso de XP"
                  valueText={`${xpProgress.xpInCurrentLevel.toLocaleString('pt-BR')} / ${XP_PER_LEVEL.toLocaleString('pt-BR')} XP`}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex shrink-0 items-center gap-1.5">

          {/* Coins */}
          <button
            type="button"
            className="flex h-11 items-center gap-2 rounded-md border border-coin/35 bg-card px-4 text-xs font-bold text-coin transition-colors hover:border-coin/60 hover:bg-coin/10 md:h-8 md:gap-1.5 md:px-2.5"
            onClick={() => router.push('/emporium/shop')}
            aria-label="Moedas"
          >
            <Sparkles className="size-4 shrink-0 md:size-3" aria-hidden="true" />
            {coinsLabel}
          </button>

          {/* Notifications */}
          <span className="inline-flex size-11 items-center justify-center md:size-8">
            <NotificationCenter
              compact
              className="relative flex size-8 items-center justify-center rounded-md border border-border bg-card text-muted-foreground transition-colors hover:border-primary/50 hover:text-primary after:absolute after:-inset-1.5 after:content-[''] md:after:hidden"
            />
          </span>

          {/* Avatar opens top drawer */}
          <span className="inline-flex size-11 items-center justify-center md:size-8">
            <Drawer open={profileOpen} onOpenChange={setProfileOpen} direction="top">
              <DrawerTrigger asChild>
                <button
                  type="button"
                  className="flex size-11 items-center justify-center rounded-full transition-opacity hover:opacity-80 md:size-8"
                  aria-label="Abrir menu do perfil"
                  onClick={() => setProfileOpen(true)}
                >
                  <img
                    src={avatarSrc}
                    alt={
                      user.displayName
                        ? `Avatar de ${user.displayName}`
                        : `Avatar do usuário @${user.username}`
                    }
                    loading="lazy"
                    decoding="async"
                    className="size-8 rounded-full border border-primary/40 object-cover"
                  />
                </button>
              </DrawerTrigger>
              <DrawerContent
                aria-label="Menu do perfil"
                className="lg:hidden pt-[env(safe-area-inset-top,0)]"
              >
                <DrawerTitle className="sr-only">Menu do perfil</DrawerTitle>
                <DrawerDescription className="sr-only">Acesso ao perfil, navegação e sair da conta</DrawerDescription>

                {/* User info */}
                <div className="flex items-center gap-3 border-b border-border px-4 py-3">
                  <img
                    src={avatarSrc}
                    alt={
                      user.displayName
                        ? `Avatar de ${user.displayName}`
                        : `Avatar do usuário @${user.username}`
                    }
                    loading="lazy"
                    decoding="async"
                    className="size-12 rounded-full border border-primary/40 object-cover"
                  />
                  <div className="min-w-0">
                    <p className="truncate font-bold text-foreground">{user.displayName}</p>
                    <p className="text-xs font-black text-primary uppercase">@{user.username}</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      Nv.{currentLevel} · Eterno {user.lifetimeLevel}
                      {user.hasPremium ? ' · Pass ativo' : ''}
                    </p>
                  </div>
                </div>

                {/* Menu items */}
                <nav className="flex flex-col gap-0.5 p-2">
                  {/* PWA Install CTA — mobile only */}
                  {pwa.isMobile && (
                    <DrawerClose asChild>
                      <button
                        type="button"
                        onClick={() => {
                          setProfileOpen(false)
                          setTimeout(() => setPwaModalOpen(true), 200)
                        }}
                        className={`flex min-h-[52px] items-center gap-3 rounded-lg px-3 text-sm font-bold transition-colors ${
                          !isPwaActivated
                            ? 'border border-primary/40 bg-tint-primary-subtle text-primary hover:bg-tint-primary-subtle/80'
                            : 'text-foreground hover:bg-muted/60'
                        }`}
                      >
                        <span className="relative">
                          <Smartphone
                            className={`size-5 shrink-0 ${!isPwaActivated ? 'text-primary' : 'text-muted-foreground'}`}
                            aria-hidden="true"
                          />
                          {!isPwaActivated && (
                            <span className="absolute -right-0.5 -top-0.5 size-2 animate-pulse rounded-full bg-success ring-1 ring-background" />
                          )}
                        </span>
                        <span aria-hidden="true">📲</span> Adicione o APP no seu Celular
                      </button>
                    </DrawerClose>
                  )}

                  {profileItems.map((item) => (
                    <DrawerClose asChild key={item.href}>
                      <Link
                        href={item.href}
                        className="flex min-h-[52px] items-center gap-3 rounded-lg px-3 text-sm font-bold text-foreground transition-colors hover:bg-muted/60"
                      >
                        <item.icon className="size-5 shrink-0 text-muted-foreground" aria-hidden="true" />
                        {item.label}
                      </Link>
                    </DrawerClose>
                  ))}
                </nav>

                <div className="mx-4 h-px bg-border" />

                {/* Sign out — destructive */}
                <div className="p-2">
                  <button
                    type="button"
                    className="flex min-h-[52px] w-full items-center gap-3 rounded-lg px-3 text-sm font-bold text-destructive transition-colors hover:bg-destructive/10"
                    onClick={handleSignOut}
                  >
                    <LogOut className="size-5 shrink-0" aria-hidden="true" />
                    Sair da conta
                  </button>
                </div>

                {/* Drag handle for top drawer */}
                <div className="mx-auto mb-3 mt-1 h-1.5 w-16 rounded-full bg-muted" />
              </DrawerContent>
            </Drawer>
          </span>
        </div>
      </div>

      {/* PWA Install Modal — lazy-mounted on first open to keep it out of the header chunk */}
      {pwaModalOpen && (
        <React.Suspense fallback={null}>
          <PwaInstallModal
            open={pwaModalOpen}
            onOpenChange={setPwaModalOpen}
            pwa={pwa}
            isPwaActivated={isPwaActivated}
          />
        </React.Suspense>
      )}

    </header>
  )
}
